import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Upload as UploadIcon } from "lucide-react";
import { useId, useState } from "react";

export interface FileDropzoneProps {
  /** Shown as the primary dropzone instruction (dropzone variant only). */
  label?: string;
  /**
   * Shown under the choose-file control in button variant. Useful for mobile
   * hints about Photos, Files, or email downloads.
   */
  helperText?: string;
  /**
   * `dropzone` keeps the dashed drag target (desktop). `button` is a plain
   * choose-file control for touch devices where drag-and-drop is not useful.
   */
  variant?: "dropzone" | "button";
  /** Label for the choose-file control. */
  chooseFileLabel?: string;
  /**
   * `default` is the tall spreadsheet-style target. `compact` is a single row
   * for tight forms that still need drag-and-drop.
   */
  density?: "default" | "compact";
  /** Accessible name for the hidden file input. */
  inputAriaLabel?: string;
  /** Accept more than one file from the picker or a drop. */
  multiple?: boolean;
  accept?: string;
  /**
   * Optional HTML `capture` hint. Prefer omitting this when the native picker
   * should offer camera, photo library, and files (typical mobile media
   * uploads). Setting `capture` often forces camera-only on mobile browsers.
   */
  capture?: boolean | "user" | "environment";
  disabled?: boolean;
  inputId?: string;
  className?: string;
  onFile?: (file: File) => void;
  /** Called with every accepted file when `multiple` is set. */
  onFiles?: (files: File[]) => void;
  /**
   * Optional validator. Return an error message to reject the file, or null to
   * accept it.
   */
  validateFile?: (file: File) => string | null;
  onInvalidFile?: (message: string) => void;
}

/**
 * Select-or-drag file upload widget used across hera-ui (answer docs, CSV
 * imports, etc.). Presentational only — callers own upload API behavior.
 *
 * The native `<input type="file">` is visually hidden behind "Choose file" so
 * desktop never shows "No file chosen", while mobile taps still open the OS
 * picker (camera / library / files when `accept` allows and `capture` is unset).
 */
export function FileDropzone({
  label = "Drag and drop a file here",
  helperText,
  variant = "dropzone",
  chooseFileLabel = "Choose file",
  density = "default",
  inputAriaLabel,
  multiple = false,
  accept,
  capture,
  disabled = false,
  inputId,
  className,
  onFile,
  onFiles,
  validateFile,
  onInvalidFile,
}: FileDropzoneProps) {
  const generatedId = useId();
  const id = inputId ?? `file-dropzone-${generatedId}`;
  const [isDragActive, setIsDragActive] = useState(false);

  const takeFiles = (list: FileList | File[] | undefined) => {
    if (!list || disabled) return;
    const incoming = Array.from(list);
    if (incoming.length === 0) return;
    const candidates = multiple ? incoming : incoming.slice(0, 1);
    const accepted: File[] = [];
    for (const file of candidates) {
      const error = validateFile?.(file) ?? null;
      if (error) {
        onInvalidFile?.(error);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    if (onFiles) {
      onFiles(accepted);
      return;
    }
    for (const file of accepted) onFile?.(file);
  };

  const fileInput = (
    <input
      id={id}
      type="file"
      multiple={multiple}
      aria-label={inputAriaLabel}
      accept={accept}
      capture={capture === true ? "environment" : capture || undefined}
      className="sr-only"
      disabled={disabled}
      onChange={(e) => {
        takeFiles(e.target.files ?? undefined);
        e.target.value = "";
      }}
    />
  );

  if (variant === "button") {
    return (
      <div className={cn("space-y-2", className)}>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={disabled}
          asChild
        >
          <label htmlFor={id} className="cursor-pointer">
            {fileInput}
            <UploadIcon className="h-4 w-4" aria-hidden />
            {chooseFileLabel}
          </label>
        </Button>
        {helperText ? (
          <p className="text-xs text-muted-foreground">{helperText}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-md border-2 border-dashed text-center transition-colors",
        density === "compact" ? "px-3 py-2" : "p-6",
        isDragActive ? "border-primary bg-primary/10" : "border-muted",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragActive(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragActive(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragActive(false);
        takeFiles(e.dataTransfer.files);
      }}
    >
      <div
        className={cn(
          "flex items-center justify-center",
          density === "compact"
            ? "flex-wrap gap-x-2 gap-y-1"
            : "flex-col gap-2",
        )}
      >
        <UploadIcon
          className={cn(
            "text-muted-foreground",
            density === "compact" ? "h-4 w-4" : "mb-2 h-8 w-8",
          )}
        />
        <span className={density === "compact" ? "text-sm" : "font-medium"}>
          {label}
        </span>
        <span className="text-xs text-muted-foreground">or</span>
        <label htmlFor={id} className="inline-block cursor-pointer">
          {/* Native input (not the styled Input) so mobile file/camera pickers work reliably. */}
          {fileInput}
          <span className="text-primary underline">{chooseFileLabel}</span>
        </label>
      </div>
      {helperText ? (
        <p
          className={cn(
            "text-xs text-muted-foreground",
            density === "compact" ? "mt-1" : "mt-2",
          )}
        >
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
